import React, { useEffect, useState } from "react";
import * as THREE from "three";
import { useTranslation } from "react-i18next";

import "./StoreySelectorComponent.css";
import { useProductModelStore } from "../../store/productModelStore";
import { useViewerStateStore } from "../../store/viewerStateStore";
import { useCameraControlsStore } from "../../store/cameraControlsStore";
import { StageTypeEnum, ViewerModeEnum } from "../../store/viewerState/ViewerMode/ViewerMode";
import { useStoreyVisibilityStore } from "../../store/storeyVisibilityStore";
import { findFromProducts } from "../../utils/productModelUtils";
import { ElementTypeEnum } from "../../elementTypeMappings";
import { findProductFromSceneByGlobalId } from "../../utils/geometryUtils";
import { SceneManager } from "../../model/SceneManager";
import Product from "../../model/Product";

const getOrdinal = (n: number, language: string) => {
    if (language && language.startsWith('fi')) {
        return `${n}.`;
    }
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const StoreySelectorComponent = () => {
    const { t, i18n } = useTranslation();
    const { products } = useProductModelStore();
    const { viewerMode } = useViewerStateStore();
    const { controls } = useCameraControlsStore();
    const { setHiddenStoreyGlobalIds } = useStoreyVisibilityStore();

    const [storeys, setStoreys] = useState<Product[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);

    // Build sorted storey list whenever products change
    useEffect(() => {
        if (products.length === 0) {
            setStoreys([]);
            setSelectedIndex(-1);
            return;
        }

        const found = findFromProducts(products, [ElementTypeEnum.Storey]);
        // Sort ascending by elevation (lowest first, index 0 = ground floor)
        const sorted = [...found].sort(
            (a, b) => a.placement.location.z - b.placement.location.z
        );

        setStoreys(sorted);
        // Default: select the highest storey (last index) — all storeys visible
        setSelectedIndex(sorted.length - 1);
    }, [products]);

    // Reset all storey visibilities when switching to Stage2d
    useEffect(() => {
        if (viewerMode?.stageType !== StageTypeEnum.Stage2d) return;

        const scene = SceneManager.scene;
        if (!scene) return;

        storeys.forEach((storey) => {
            const obj = findProductFromSceneByGlobalId(scene, storey.globalId);
            if (obj) {
                obj.visible = true;
            }
        });
        setHiddenStoreyGlobalIds(new Set());
        // NOTE: selectedIndex is intentionally NOT reset here — the products effect
        // is the sole authority for initialising selectedIndex. The selector is
        // invisible while in Stage2d anyway (returns null above).
    }, [viewerMode?.stageType, storeys]);

    // Apply visibility whenever the selected storey changes
    useEffect(() => {
        if (storeys.length === 0 || selectedIndex < 0) return;

        const scene = SceneManager.scene;
        if (!scene) return;

        const hidden = new Set<string>();
        storeys.forEach((storey, idx) => {
            const obj = findProductFromSceneByGlobalId(scene, storey.globalId);
            const visible = idx <= selectedIndex;
            if (obj) {
                obj.visible = visible;
            }
            if (!visible) {
                hidden.add(storey.globalId);
            }
        });
        setHiddenStoreyGlobalIds(hidden);
    }, [selectedIndex, storeys]);

    // Do not show in Floorplan mode (ApartmentSelect2d)
    if (viewerMode?.modeEnum === ViewerModeEnum.ApartmentSelect2d) return null;
    if (products.length <= 1 || storeys.length === 0) return null;

    const setStoreyIndex = (nextIndex: number) => {
        if (storeys.length === 0 || nextIndex === selectedIndex) return;
        if (selectedIndex < 0) {
            // selectedIndex is in an invalid state; just jump directly to the target
            setSelectedIndex(nextIndex);
            return;
        }

        const currentStorey = storeys[selectedIndex];
        const nextStorey = storeys[nextIndex];
        
        const zDiff = nextStorey.placement.location.z - currentStorey.placement.location.z;

        if (controls && zDiff !== 0) {
            const camPos = new THREE.Vector3();
            const targetPos = new THREE.Vector3();
            controls.getPosition(camPos);
            controls.getTarget(targetPos);
            
            // Move camera and target by the difference
            controls.setPosition(camPos.x, camPos.y, camPos.z + zDiff, true);
            controls.setTarget(targetPos.x, targetPos.y, targetPos.z + zDiff, true);
        }

        setSelectedIndex(nextIndex);
    };

    const goUp = () => {
        if (selectedIndex < storeys.length - 1) setStoreyIndex(selectedIndex + 1);
    };

    const goDown = () => {
        if (selectedIndex > 0) setStoreyIndex(selectedIndex - 1);
    };

    const goDownOrWrap = () => {
        if (storeys.length === 0) return;
        setStoreyIndex(selectedIndex <= 0 ? storeys.length - 1 : selectedIndex - 1);
    };

    const currentStorey = storeys[selectedIndex];
    const displayNumber = selectedIndex + 1;
    const ordinal = getOrdinal(displayNumber, i18n.language);
    const displayName = currentStorey ? (t("ui.storeySelector.nameFormat", { ordinal } as any) as string) : "—";

    return (
        <div className="storey-selector" title={t("ui.storeySelector.title")}>
            <button className="storey-selector__btn" onClick={goDown} disabled={selectedIndex === 0}>
                ▼
            </button>
            <span className="storey-selector__name" onClick={goDownOrWrap}>{displayName}</span>
            <button className="storey-selector__btn" onClick={goUp} disabled={selectedIndex >= storeys.length - 1}>
                ▲
            </button>
        </div>
    );
};

export default StoreySelectorComponent;
